import "@/App.css";
import { unstable_HistoryRouter as HistoryRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
// iter264 — apply saved / OS theme BEFORE the first paint so users
// who prefer dark never see a light-mode flash on load. Safe to run
// at import time because `applyTheme` is a no-op in SSR.
import { applyTheme, resolveInitialTheme } from "@/lib/theme";
applyTheme(resolveInitialTheme());
import Layout from "@/components/Layout";
import COReview from "@/pages/COReview";
import ProposalReview from "@/pages/ProposalReview";
import WOReview from "@/pages/WOReview";
import POReview from "@/pages/POReview";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { initPushNotifications } from "@/lib/pushNotifications";
import { AuthProvider } from "@/auth/AuthContext";
import { I18nProvider } from "@/i18n";
import ProtectedRoute from "@/auth/ProtectedRoute";
import Login from "@/auth/Login";
import AuthCallback from "@/auth/AuthCallback";
import FieldLogin from "@/auth/FieldLogin";
import { useEffect } from "react";
import { toast } from "sonner";
import { applyServiceWorkerUpdate } from "@/lib/serviceWorker";
import { initInspectionSync } from "@/lib/inspectionSync";
import { protectedRoutes } from "@/appRoutes";
import { mainHistory } from "@/lib/splitScreen";

// Customer / vendor-facing review pages are outside the UI sweep: while one is mounted, <html> carries
// `abs-public` so they (and anything they portal) keep the previous type. Presentation only.
function PublicPage({ children }) {
  useEffect(() => {
    document.documentElement.classList.add("abs-public");
    return () => document.documentElement.classList.remove("abs-public");
  }, []);
  return children;
}

function App() {
  // Boot the MIRA console-capture ring buffer once at app boot so the
  // feedback widget can ship the last ~50 console lines as telemetry
  // when a user files a ticket.
  useEffect(() => {
    import("@/components/feedback/consoleCapture").then((m) => m.startConsoleCapture());
    // Offline inspection sync: boots app-wide so a queued inspection
    // uploads the moment signal returns, from whatever screen the tech
    // happens to be on (including a cold start after a phone reboot).
    initInspectionSync();
    // Native shell push (Capacitor) + browser/PWA web push foreground
    // toasts. Both are no-ops until the Firebase keys are in place.
    initPushNotifications({
      getUserId: () => {
        try { return JSON.parse(localStorage.getItem("user") || "{}")?.id || null; }
        catch { return null; }
      },
    });
    let unsubWebPush;
    import("@/lib/webPush").then(async (m) => {
      unsubWebPush = await m.listenForegroundWebPush();
    });
    return () => { if (typeof unsubWebPush === "function") unsubWebPush(); };
  }, []);

  // Service-worker "new version waiting" prompt. Surfaces as a sonner
  // toast with a "Refresh" action — tapping it activates the waiting
  // SW and reloads the page so techs always run the latest shell.
  //
  // iter186: handle the race where `sw:update-available` fires from
  // /lib/serviceWorker.js BEFORE React mounts (which is the common
  // case on a hard reload). Inside the useEffect we also poll the
  // registration once — if a waiting worker already exists, fire the
  // toast manually so the user still gets the prompt.
  useEffect(() => {
    const onUpdate = () => {
      toast("New version available", {
        description: "Refresh to get the latest fixes and features.",
        action: { label: "Refresh", onClick: () => applyServiceWorkerUpdate() },
        duration: Infinity,
        id: "sw-update",
      });
    };
    window.addEventListener("sw:update-available", onUpdate);
    // Catch-up check for the missed-event race.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting && navigator.serviceWorker.controller) {
          onUpdate();
        }
      }).catch(() => {});
    }
    return () => window.removeEventListener("sw:update-available", onUpdate);
  }, []);

  return (
    <div className="app-layout">
      {/* HistoryRouter over our own browser history so the split screen can route record opens into the right pane. */}
      <HistoryRouter history={mainHistory}>
        <AuthProvider>
          <I18nProvider>
          <Routes>
            {/* Public — no auth required */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/field-login" element={<FieldLogin />} />
            <Route path="co-review/:token/:doc?" element={<PublicPage><COReview /></PublicPage>} />
            <Route path="proposal-review/:token/:doc?" element={<PublicPage><ProposalReview /></PublicPage>} />
            <Route path="wo-review/:token/:doc?" element={<PublicPage><WOReview /></PublicPage>} />
            {/* #65 — Vendor-facing PO review page (no auth; short_code is bearer) */}
            <Route path="po-review/:code" element={<PublicPage><POReview /></PublicPage>} />

            {/* Protected — everything else (route tree shared with the split-screen pane, see appRoutes.jsx) */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {protectedRoutes()}
            </Route>
          </Routes>
          </I18nProvider>
        </AuthProvider>
      </HistoryRouter>
      <Toaster position="bottom-right" />
      {/* #55 — PWA install banner (mobile only; iOS Safari shows manual hint) */}
      <PWAInstallBanner />
    </div>
  );
}

export default App;
